'use strict';

/** Max viewport width (px) to enable drag-to-close; tablet and below (e.g. < 1024). */
var TABLET_BREAKPOINT = 1024;
var THRESHOLD_RATIO = 0.3;
var ANIMATION_DURATION = 300;
var MODAL_DRAG_SELECTOR = '.modal.show[data-drag-close="modal"]';

/**
 * Initialize drag-to-close on a popup element.
 * Used for non-modal popups (Sirv hotspots, pannellum, etc.).
 * Modal drag-to-close is handled separately by initModalDragClose().
 *
 * @param {HTMLElement} contentEl - The popup content element that receives transform
 * @param {Object} opts
 * @param {Function} opts.onClose - Callback when drag exceeds threshold
 * @param {string} [opts.scrollableSelector] - Selector for the scrollable body inside contentEl
 * @param {number} [opts.threshold=0.3] - Ratio of popup height to trigger close (0-1)
 * @param {number} [opts.breakpoint=1024] - Max viewport width to enable drag (tablet and below)
 * @param {function(): boolean} [opts.allowDragClose] - If provided and returns false, drag-to-close is ignored (no transform / no onClose)
 * @returns {Function} destroy - Call to remove all listeners
 */
function initDragToClose(contentEl, opts) {
    var threshold = opts.threshold || THRESHOLD_RATIO;
    var breakpoint = opts.breakpoint || TABLET_BREAKPOINT;
    var startY = 0;
    var currentY = 0;
    var isDragging = false;

    function isBelowBreakpoint() {
        return window.innerWidth < breakpoint;
    }

    function getScrollable() {
        if (typeof opts.scrollableSelector === 'function') {
            return opts.scrollableSelector();
        } else if (typeof opts.scrollableSelector === 'string') {
            return contentEl.querySelector(opts.scrollableSelector);
        } else {
            return (opts.scrollableSelector || null);
        }
    }

    function clearInlineStyles() {
        contentEl.style.removeProperty('transform');
        contentEl.style.removeProperty('transition');
        contentEl.style.removeProperty('animation');
    }

    function onTouchStart(e) {
        if (!isBelowBreakpoint()) return;

        if (typeof opts.allowDragClose === 'function' && !opts.allowDragClose()) {
            return;
        }

        var scrollable = getScrollable();
        isDragging = !scrollable || scrollable.scrollTop === 0;
        if (!isDragging) return;

        startY = e.touches[0].clientY;
        currentY = startY;

        var computedTransform = window.getComputedStyle(contentEl).transform;
        contentEl.style.setProperty('transform', computedTransform || 'translateY(0)', 'important');
        contentEl.style.setProperty('animation', 'none', 'important');
        contentEl.style.setProperty('transition', 'none', 'important');
    }

    function onTouchMove(e) {
        if (!isDragging) return;

        currentY = e.touches[0].clientY;
        var deltaY = currentY - startY;

        if (deltaY > 0) {
            contentEl.style.setProperty('transform', 'translateY(' + deltaY + 'px)', 'important');
            e.preventDefault();
        } else {
            isDragging = false;
            clearInlineStyles();
        }
    }

    function onTouchEnd() {
        if (!isDragging) return;
        isDragging = false;

        var deltaY = currentY - startY;

        var shouldClose = deltaY > contentEl.offsetHeight * threshold;

        contentEl.style.setProperty('transition', 'transform ' + ANIMATION_DURATION + 'ms ease-out', 'important');
        contentEl.style.setProperty('transform', shouldClose ? 'translateY(100%)' : 'translateY(0)', 'important');

        setTimeout(function () {
            if (shouldClose && typeof opts.onClose === 'function') {
                opts.onClose();
            }
            clearInlineStyles();
        }, ANIMATION_DURATION);
    }

    contentEl.addEventListener('touchstart', onTouchStart, {passive: true});
    contentEl.addEventListener('touchmove', onTouchMove, {passive: false});
    contentEl.addEventListener('touchend', onTouchEnd);

    return function destroy() {
        contentEl.removeEventListener('touchstart', onTouchStart);
        contentEl.removeEventListener('touchmove', onTouchMove);
        contentEl.removeEventListener('touchend', onTouchEnd);
        clearInlineStyles();
    };
}

// ---------------------------------------------------------------------------
//  Modal drag-to-close — document-level event delegation
// ---------------------------------------------------------------------------

var activeDrag = null;

function clearDragStyles(target) {
    target.style.removeProperty('transform');
    target.style.removeProperty('transition');
    target.style.removeProperty('animation');
}

function closeModal(modal, target) {
    clearDragStyles(target);
    modal.classList.remove('fade');
    if (window.$ && window.$(modal).modal) {
        window.$(modal).modal('hide');
    }
    modal.classList.add('fade');

    if (document.querySelector('.modal.show')) {
        document.body.classList.add('modal-open');
    }
}

function onModalTouchStart(e) {
    if (activeDrag) return;
    if (window.innerWidth >= TABLET_BREAKPOINT) return;

    var modal = e.target.closest(MODAL_DRAG_SELECTOR);
    if (!modal || !modal._dragCloseConfig) return;

    var cfg = modal._dragCloseConfig;
    var target = modal.querySelector(cfg.targetSelector);
    if (!target) return;

    var scrollable = cfg.scrollableSelector
        ? modal.querySelector(cfg.scrollableSelector)
        : null;
    if (scrollable && scrollable.scrollTop > 0) return;
    if (modal.scrollTop > 0) return;

    activeDrag = {
        modal: modal,
        target: target,
        threshold: cfg.threshold,
        startY: e.touches[0].clientY,
        currentY: e.touches[0].clientY,
        started: false
    };
}

function onModalTouchMove(e) {
    if (!activeDrag) return;

    activeDrag.currentY = e.touches[0].clientY;
    var deltaY = activeDrag.currentY - activeDrag.startY;

    if (deltaY > 0) {
        if (!activeDrag.started) {
            activeDrag.started = true;
            var computedTransform = window.getComputedStyle(activeDrag.target).transform;
            activeDrag.target.style.setProperty('transform', computedTransform || 'translateY(0)', 'important');
            activeDrag.target.style.setProperty('animation', 'none', 'important');
            activeDrag.target.style.setProperty('transition', 'none', 'important');
        }
        activeDrag.target.style.setProperty('transform', 'translateY(' + deltaY + 'px)', 'important');
        e.preventDefault();
    } else {
        if (activeDrag.started) {
            clearDragStyles(activeDrag.target);
        }
        activeDrag = null;
    }
}

function onModalTouchEnd() {
    if (!activeDrag) return;

    var drag = activeDrag;
    activeDrag = null;

    if (!drag.started) return;

    var deltaY = drag.currentY - drag.startY;
    var shouldClose = deltaY > drag.target.offsetHeight * drag.threshold;

    drag.target.style.setProperty('transition', 'transform ' + ANIMATION_DURATION + 'ms ease-out', 'important');
    drag.target.style.setProperty('transform', shouldClose ? 'translateY(100%)' : 'translateY(0)', 'important');

    setTimeout(function () {
        if (shouldClose) {
            closeModal(drag.modal, drag.target);
        } else {
            clearDragStyles(drag.target);
        }
    }, ANIMATION_DURATION);
}

/**
 * Set up document-level touch handlers for modal drag-to-close.
 * Handles all modals with [data-drag-close="modal"] via event delegation.
 */
function initModalDragClose() {
    document.addEventListener('touchstart', onModalTouchStart, {passive: false});
    document.addEventListener('touchmove', onModalTouchMove, {passive: false});
    document.addEventListener('touchend', onModalTouchEnd);
}

// ---------------------------------------------------------------------------
//  Sirv / generic popup helpers (unchanged)
// ---------------------------------------------------------------------------

/**
 * Attach drag-to-close to a popup wrapper.
 * Clicks the native close button so the host library (Sirv) updates its
 * internal state and can re-open the popup on the next hotspot click.
 *
 * @param {HTMLElement} wrapper - The popup wrapper element
 * @param {Object} opts - Options forwarded to initDragToClose
 * @param {string} overlaySelector - CSS selector for the overlay ancestor
 */
function attachPopupDrag(wrapper, opts, overlaySelector) {
    initDragToClose(wrapper, {
        scrollableSelector: opts.scrollableSelector,
        breakpoint: opts.breakpoint,
        threshold: opts.threshold,
        onClose: function () {
            var closeBtn = wrapper.querySelector('.sirv-hotspot-close-button');
            if (closeBtn) {
                closeBtn.click();
            } else {
                var overlay = wrapper.closest(overlaySelector);
                if (overlay) {
                    overlay.remove();
                }
            }
        }
    });
}

/**
 * Auto-observe DOM for popup elements and attach drag-to-close.
 * On drag close the overlay is hidden (not removed), preserving the
 * element for the host library (e.g. Sirv) to re-show on next open.
 *
 * @param {Object} opts
 * @param {string} opts.selector - CSS selector for the popup wrapper
 * @param {string} [opts.overlaySelector='.sirv-hotspot-box'] - Overlay ancestor selector
 * @param {string} [opts.scrollableSelector] - Scrollable body selector inside the wrapper
 * @param {number} [opts.breakpoint=1024] - Max viewport width to enable drag (tablet and below)
 * @param {number} [opts.threshold=0.3] - Ratio of popup height to trigger close
 * @returns {Function} destroy - Disconnects the observer
 */
function observePopups(opts) {
    var selector = opts.selector;
    var overlaySelector = opts.overlaySelector || '.sirv-hotspot-box';

    function processNode(node) {
        if (node.nodeType !== 1) return;

        var wrapper;

        if (node.matches && node.matches(selector)) {
            wrapper = node;
        } else if (node.querySelector) {
            wrapper = node.querySelector(selector);
        }

        if (!wrapper || wrapper._popupDragInit) return;

        wrapper._popupDragInit = true;
        attachPopupDrag(wrapper, opts, overlaySelector);
    }

    var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
            var addedNodes = mutations[i].addedNodes;
            for (var j = 0; j < addedNodes.length; j++) {
                processNode(addedNodes[j]);
            }
        }
    });

    observer.observe(document.body, {childList: true, subtree: true});

    return function destroy() {
        observer.disconnect();
    };
}

/**
 * Resolve the close action for a declarative drag-close element.
 *
 * @param {HTMLElement} el - The element with [data-drag-close]
 * @param {string} mode - Close strategy: 'flyout' | 'modal' | 'hide' | 'remove' | 'dismiss'
 */
function resolveClose(el, mode) {
    switch (mode) {
        case 'modal':
            if (window.$ && window.$(el).modal) {
                window.$(el).modal('hide');
            }
            break;
        case 'dismiss':
            var btn = el.querySelector('.sirv-hotspot-close-button') || el.querySelector('.close');
            if (btn) {
                btn.click();
            }
            break;
        case 'hide':
            el.style.display = 'none';
            break;
        case 'remove':
            el.remove();
            break;
        case 'flyout':
        default:
            el.classList.remove('open');
            document.body.style.overflow = '';
            break;
    }
}

// ---------------------------------------------------------------------------
//  Global auto-attach
// ---------------------------------------------------------------------------

/**
 * Process a single [data-drag-close] element: read config from data
 * attributes and either store config (modal) or attach initDragToClose
 * (non-modal).
 *
 * @param {HTMLElement} el - Element with [data-drag-close]
 */
function processGlobalDragNode(el) {
    if (el._popupDragInit) return;

    var mode = el.getAttribute('data-drag-close') || 'flyout';
    var targetSelector = el.getAttribute('data-drag-close-target');
    var scrollableSelector = el.getAttribute('data-drag-close-scrollable');
    var breakpoint = parseInt(el.getAttribute('data-drag-close-breakpoint'), 10) || TABLET_BREAKPOINT;
    var threshold = parseFloat(el.getAttribute('data-drag-close-threshold')) || THRESHOLD_RATIO;

    el._popupDragInit = true;

    if (mode === 'modal') {
        el._dragCloseConfig = {
            targetSelector: targetSelector || '.modal-dialog',
            scrollableSelector: scrollableSelector,
            breakpoint: breakpoint,
            threshold: threshold
        };
        return;
    }

    var target = targetSelector ? el.querySelector(targetSelector) : el;
    if (!target) return;

    initDragToClose(target, {
        scrollableSelector: scrollableSelector,
        breakpoint: breakpoint,
        threshold: threshold,
        onClose: function () {
            resolveClose(el, mode);
        }
    });
}

/**
 * Global auto-attach: observe the DOM for [data-drag-close] elements and
 * wire up drag-to-close automatically.  Call once at application startup.
 *
 * Existing elements are processed immediately; dynamically added elements
 * (e.g. pannellum popups appended to <body>) are caught by a
 * MutationObserver watching childList + subtree.
 *
 * @returns {Function} destroy - Disconnects the observer
 */
function initGlobalDragClose() {
    var selector = '[data-drag-close]';

    var existing = document.querySelectorAll(selector);
    for (var i = 0; i < existing.length; i++) {
        processGlobalDragNode(existing[i]);
    }

    initModalDragClose();

    var observer = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
            var addedNodes = mutations[m].addedNodes;
            for (var n = 0; n < addedNodes.length; n++) {
                var node = addedNodes[n];
                if (node.nodeType !== 1) continue;

                if (node.hasAttribute && node.hasAttribute('data-drag-close')) {
                    processGlobalDragNode(node);
                }

                if (node.querySelectorAll) {
                    var children = node.querySelectorAll(selector);
                    for (var c = 0; c < children.length; c++) {
                        processGlobalDragNode(children[c]);
                    }
                }
            }
        }
    });

    observer.observe(document.body, {childList: true, subtree: true});

    return function destroy() {
        observer.disconnect();
    };
}

initDragToClose.observePopups = observePopups;
initDragToClose.initGlobalDragClose = initGlobalDragClose;
initDragToClose.TABLET_BREAKPOINT = TABLET_BREAKPOINT;
module.exports = initDragToClose;
